import React, { useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  IconButton,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Analytics as AnalyticsIcon,
  Assessment as ResultsIcon,
  Settings as SettingsIcon,
  AccountCircle,
  Logout,
  Hub as NetworkIcon,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  BugReport as BugReportIcon,
  //MenuBook as UserGuideIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useDateRange } from '../../contexts/DateRangeContext';
import DateRangePicker from './DateRangePicker';
import CSVUploadDialog from '../Pathfinder/CSVUploadDialog';
import { PathfinderDataPoint } from '../../services/pathfinderApi';

const allMenuItems = [
  { 
    text: 'OSINT Explorer', 
    icon: <Box component="img" src="/Circle_Endurion_White.png" alt="OSINT Explorer" sx={{ width: 24, height: 24 }} />, 
    path: '/pathfinder' 
  },
  { text: 'Upload Data', icon: <UploadIcon />, path: null }, // Changed to trigger CSV dialog instead
  { text: 'Analysis', icon: <AnalyticsIcon />, path: '/analysis' },
  { text: 'Scheduled Analysis', icon: <ScheduleIcon />, path: '/scheduled-analyses' },
  { text: 'Results', icon: <ResultsIcon />, path: '/results' },
  { text: 'Link Analysis', icon: <NetworkIcon />, path: '/link-analysis' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
];

function getMenuItemsForRole(user: any) {
  if (!user || !user.roles) return allMenuItems;
  if (user.roles.includes('student')) {
    return []; // Students have no menu items
  }
  return allMenuItems;
}

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { startDate, endDate, setDateRange } = useDateRange();

  const menuItems = getMenuItemsForRole(user);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [csvUploadDialogOpen, setCSVUploadDialogOpen] = useState(false);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  const handleLoadFiles = (files: { dataPoints: PathfinderDataPoint[]; fileName: string }[], mode: 'as-one' | 'separate') => {
    setCSVUploadDialogOpen(false);
    const payload = { files, mode };
    if (location.pathname !== '/pathfinder') {
      sessionStorage.setItem('csvUploadData', JSON.stringify(payload));
      navigate('/pathfinder');
    } else {
      sessionStorage.setItem('csvUploadData', JSON.stringify(payload));
      window.dispatchEvent(new Event('csv-data-available'));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />
      
      {/* Main AppBar with Top Navigation */}
      <AppBar
        position="fixed"
        sx={{
          top: 0,
          backgroundColor: '#03111E',
          zIndex: 1200,
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {/* Left: Logo and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box 
              component="img"
              src="/Circle_Endurion_White.png"
              alt="OSINT Explorer"
              sx={{ 
                height: '40px', 
                width: 'auto',
              }}
            />
            <Typography variant="h6" noWrap component="div" sx={{ color: 'white', fontWeight: 500 }}>
              Pathfinder: OSINT Explorer
            </Typography>
          </Box>
          
          {/* Center: Navigation Icons and Date Picker */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Tracer Icon (first) */}
            {menuItems.slice(0, 1).map((item) => (
              <Tooltip key={item.text} title={item.text} arrow>
                <IconButton
                  onClick={() => {
                    if (item.path) navigate(item.path);
                  }}
                  sx={{
                    color: item.path && location.pathname === item.path ? 'primary.light' : 'rgba(255, 255, 255, 0.7)',
                    backgroundColor: item.path && location.pathname === item.path ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(33, 150, 243, 0.2)',
                      color: 'white',
                    },
                    borderRadius: 1,
                    transition: 'all 0.2s',
                  }}
                >
                  {item.icon}
                </IconButton>
              </Tooltip>
            ))}
            
            {/* Date Range Picker - Between Tracer and Upload */}
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onDateRangeChange={setDateRange}
            />
            
            {/* Rest of Navigation Icons */}
            {menuItems.slice(1).map((item) => {
              // Upload Data is only available on OSINT Explorer page
              const isUploadButton = item.text === 'Upload Data';
              const isOnTracerPage = location.pathname === '/' || location.pathname === '/pathfinder';
              const isUploadDisabled = isUploadButton && !isOnTracerPage;
              
              return (
                <Tooltip 
                  key={item.text} 
                  title={isUploadDisabled ? 'Upload Data is only available on the OSINT Explorer page' : item.text} 
                  arrow
                >
                  <span> {/* Wrapper needed for disabled button tooltip */}
                    <IconButton
                      disabled={isUploadDisabled}
                      onClick={() => {
                        if (item.path) {
                          navigate(item.path);
                        } else if (item.text === 'Upload Data') {
                          setCSVUploadDialogOpen(true);
                        }
                      }}
                      sx={{
                        color: item.path && location.pathname === item.path ? 'primary.light' : 
                               isUploadDisabled ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.7)',
                        backgroundColor: item.path && location.pathname === item.path ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
                        '&:hover': {
                          backgroundColor: isUploadDisabled ? 'transparent' : 'rgba(33, 150, 243, 0.2)',
                          color: isUploadDisabled ? 'rgba(255, 255, 255, 0.3)' : 'white',
                        },
                        '&.Mui-disabled': {
                          color: 'rgba(255, 255, 255, 0.3)',
                        },
                        borderRadius: 1,
                        transition: 'all 0.2s',
                      }}
                    >
                      {item.icon}
                    </IconButton>
                  </span>
                </Tooltip>
              );
            })}
          </Box>
          
          {/* Right: User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'white' }}>
              {user?.email}
            </Typography>
            <IconButton
              size="large"
              aria-label="account menu"
              aria-controls="account-menu"
              aria-haspopup="true"
              onClick={handleMenuClick}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
                <AccountCircle />
              </Avatar>
            </IconButton>
            <Menu
              id="account-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              onClick={handleMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>

            <Tooltip title="Contact Support">
              <IconButton
                color="inherit"
                href="mailto:customersupport@quietprofessionalsllc.com"
                sx={{ ml: 1 }}
              >
                <EmailIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Report a Bug">
              <IconButton
                color="inherit"
                href="https://forms.office.com/pages/responsepage.aspx?id=U_f36xaULUusxquLpPPgyoYya6uIVt9Fu47_c5d2VIxUOFlIT1UzWUM4VThKTElMT0xIUjhCVTFERS4u&route=shorturl"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ ml: 1, mr: 1 }}
              >
                <BugReportIcon />
              </IconButton>
            </Tooltip>
            
            {/*<Tooltip title="User Guide">
              <IconButton
                color="inherit"
                onClick={() => window.open('https://signals-tracer.tracer.p.azurewebsites.net/auth/login', '_blank')}
                sx={{ ml: 1, mr: 1 }}
              >
                <UserGuideIcon />
              </IconButton>
            </Tooltip>*/}
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: '64px', // AppBar height only
          px: 0,
          backgroundColor: 'background.default',
          minHeight: '100vh',
          width: '100%',
        }}
      >
          {children}
      </Box>

      {/* CSV Upload Dialog */}
      <CSVUploadDialog
        open={csvUploadDialogOpen}
        onClose={() => setCSVUploadDialogOpen(false)}
        onLoadFiles={handleLoadFiles}
      />
    </Box>
  );
};

export default Layout;
